import { Pressable, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useTheme } from '@/theme';
import { SubHeading, ThemedText } from '@/components/ui/Text';

type ModalHeaderProps = {
  title: string;
  onClose: () => void;
  leftActionLabel?: string;
  onLeftAction?: () => void;
  disableClose?: boolean;
  disableLeftAction?: boolean;
  borderColor?: string;
};

/**
 * Shared full-screen modal header with safe-area spacing and a close affordance.
 */
export function ModalHeader({
  title,
  onClose,
  leftActionLabel,
  onLeftAction,
  disableClose = false,
  disableLeftAction = false,
  borderColor,
}: ModalHeaderProps) {
  const theme = useTheme();
  const insets = useSafeAreaInsets();

  const leftAction = leftActionLabel && onLeftAction ? (
    <Pressable
      onPress={onLeftAction}
      disabled={disableLeftAction}
      accessibilityRole="button"
      accessibilityLabel={leftActionLabel}
      style={({ pressed }) => ({
        minWidth: 72,
        opacity: disableLeftAction ? 0.5 : pressed ? 0.7 : 1,
      })}
    >
      <ThemedText variant="bodyStrong" color={theme.colors.primary}>
        {leftActionLabel}
      </ThemedText>
    </Pressable>
  ) : (
    <View style={{ minWidth: 40 }} />
  );

  return (
    <View
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingHorizontal: theme.spacing(2),
        paddingBottom: theme.spacing(2),
        paddingTop: insets.top + theme.spacing(1),
        borderBottomWidth: 1,
        borderBottomColor: borderColor || theme.colors.borderMuted,
      }}
    >
      {leftAction}

      <SubHeading style={{ flex: 1, textAlign: 'center' }}>{title}</SubHeading>

      <Pressable
        onPress={onClose}
        disabled={disableClose}
        accessibilityRole="button"
        accessibilityLabel="Close"
        style={({ pressed }) => ({
          padding: theme.spacing(1),
          borderRadius: theme.radii.md,
          opacity: disableClose ? 0.5 : pressed ? 0.7 : 1,
        })}
      >
        <Ionicons name="close" size={24} color={theme.colors.text} />
      </Pressable>
    </View>
  );
}
